import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, keyframes} from '@mantine/core';
import type {MantineColor} from '@mantine/core';
import React from 'react';

import StatusPill from './StatusPill';

export type JobStatus = 'FAILED' | 'PENDING' | 'RUNNING' | 'SUCCEEDED';

interface StatusConfig {
  color: MantineColor;
  label: string;
}

const STATUS_CONFIG: Record<JobStatus, StatusConfig> = {
  FAILED: {color: 'red', label: 'Failed'},
  PENDING: {color: 'gray', label: 'Queued'},
  RUNNING: {color: 'blue', label: 'Running'},
  SUCCEEDED: {color: 'green', label: 'Done'},
};

const ping = keyframes({
  '0%': {opacity: 0.75, transform: 'scale(1)'},
  '100%': {opacity: 0, transform: 'scale(2.5)'},
});

interface StatusBadgeProps {
  status: JobStatus;
}

export default function StatusBadge({status}: StatusBadgeProps): JSX.Element {
  const config = STATUS_CONFIG[status];
  const {classes, cx} = useStyles({color: config.color});
  const isPulsing = status === 'RUNNING';

  return (
    <StatusPill
      {...getTestProps({component: 'StatusBadge', element: 'root', key: status})}
      classNames={{root: classes.root}}
      color={config.color}
      leftSection={<span className={cx(classes.dot, isPulsing && classes.dotPulsing)} />}
      size="sm"
    >
      {config.label}
    </StatusPill>
  );
}

const useStyles = createStyles((theme, {color}: {color: MantineColor}) => ({
  root: {
    minHeight: 18,
  },
  dot: {
    backgroundColor: theme.colors[color][6],
    borderRadius: '50%',
    flex: '0 0 6px',
    height: 6,
    position: 'relative' as const,
    width: 6,
  },
  dotPulsing: {
    '&::after': {
      animation: `${ping} 1.5s ease-out infinite`,
      backgroundColor: theme.colors[color][6],
      borderRadius: '50%',
      content: '""',
      height: '100%',
      left: 0,
      position: 'absolute' as const,
      top: 0,
      width: '100%',
    },
  },
}));
